import {Status, UrlSelection} from 'commonTypes'
import {normalizeDatabaseOptions} from 'helpers/databases'
import {createActionCreators, ImmerReducer} from 'immer-reducer'
import {
  select_objectId,
  select_dataLoadTopic,
  select_databaseId,
  select_studyUuid,
  select_jobRequestOptions,
  select_changesAfter_timeDateUTC,
  select_jobRequestCategory,
  select_specifiedUrls,
  select_urlSelectionType,
  select_dataVerificationHashObject,
  select_studyId,
} from 'redux/jobRequests/selectors'
import defaultJobRequestState, {
  JobRequestStateType,
  DatabaseOptionType,
} from 'redux/jobRequests/state'
import {getActiveAndEnabledDatabaseUrls} from 'api/graphQL/queries/databases.queries'
import {
  getJobRequestOptions,
  getJobRequests,
  getStudies,
  getStudyEnvironments} from 'api/graphQL/queries/jobRequests.queries'
import {select_gridSession} from './selectors'
import {DataVerificationHashObject, JobCreatorOptions, JobRequestInput, PageInfo, Scalars} from 'graphql-api'
import {getJobStatusRequestsParams} from './helpers'
import {RouterStates} from 'router/states/routerStates.enum'
import {
  genericRequestError,
  genericRequestSuccess,
  customMessageRequest} from '../toast/toast.duck'
import {jobStatusRequestDetails} from 'api/graphQL/queries/jobRequests.queries'
import {select_router_params} from 'redux/router/selectors'
import _isEqual from 'lodash.isequal'
import {getPageDirectionFromPageIndexChange, validateUrls} from 'helpers/common'
import {jobRequest} from 'api/graphQL/mutations/jobs.mutations'

class JobRequest extends ImmerReducer<JobRequestStateType> {

  fetchDatabaseStudyGroupUuidOptionsGO() {
    this.draftState.statusDetails = Status.PENDING
  }
  fetchDatabaseStudyGroupUuidOptionsOK(studyGroupUuidOptions: DatabaseOptionType[]) {
    this.draftState.database.databaseOptions = studyGroupUuidOptions
    this.draftState.statusDetails = Status.OK
  }
  fetchDatabaseStudyGroupUuidOptionsERR(error) {
    this.draftState.error = error
    this.draftState.statusDetails = Status.REJECTED
  }

  fetchJobRequestOptionsGO() {
    this.draftState.statusDataLoad = Status.PENDING
  }
  fetchJobRequestOptionsOK(jobRequestOptions: JobCreatorOptions[], dataVerificationHashObjectOptions: DataVerificationHashObject[]) {
    this.draftState.jobRequestOptions = jobRequestOptions
    this.draftState.dataVerificationHashObjectOptions = dataVerificationHashObjectOptions
    this.draftState.statusDataLoad = Status.OK
  }
  fetchJobRequestOptionsERR(error) {
    this.draftState.error = error
    this.draftState.statusDataLoad = Status.REJECTED
  }
  jobRequestCategoryApplied(option) {
    this.draftState.jobRequestCategory = option
  }
  jobRequestCategoryReset() {
    this.draftState.jobRequestCategory = defaultJobRequestState.jobRequestCategory
  }
  dataLoadTopicOptionsApplied(option) {
    this.draftState.dataLoadTopic = option
  }
  dataLoadTopicApplied(option) {
    this.draftState.dataLoadTopic = option
  }
  dataLoadTopicReset() {
    this.draftState.dataLoadTopic = defaultJobRequestState.dataLoadTopic
  }
  databaseApplied(databaseName: string, databaseUuid: string, selectionType: UrlSelection) {
    this.draftState.database.hostname = databaseName
    this.draftState.database.databaseId = databaseUuid
    this.draftState.urlSelectionType = selectionType
    this.studyReset()
    this.studyEnvironmentReset()
  }
  databaseReset() {
    this.draftState.database.databaseId = defaultJobRequestState.database.databaseId
    this.draftState.database.hostname = defaultJobRequestState.database.hostname
    this.draftState.specifiedUrls = defaultJobRequestState.specifiedUrls
    this.draftState.urlSelectionType = defaultJobRequestState.urlSelectionType
    this.studyReset()
    this.studyEnvironmentReset()
  }
  dataVerificationHashObjectApplied(option) {
    this.draftState.dataVerificationHashObject = option?.value || null
  }
  dataVerificationHashObjectReset() {
    this.draftState.dataVerificationHashObject = defaultJobRequestState.dataVerificationHashObject
  }
  fetchDatabaseStudyGroupStudyUuidOptionsGO() {
    this.draftState.statusDetails = Status.PENDING
  }
  fetchDatabaseStudyGroupStudyUuidOptionsOK(studyOptions: []) {
    this.draftState.study.studyOptions = studyOptions
    this.draftState.statusDetails = Status.OK
  }
  fetchDatabaseStudyGroupStudyUuidOptionsERR(error) {
    this.draftState.error = error
    this.draftState.statusDetails = Status.REJECTED
  }
  studyApplied(studyName: string, studyUuid: string) {
    this.draftState.study.studyName = studyName
    this.draftState.study.studyUuid = studyUuid
    this.studyEnvironmentReset()
  }
  studyReset() {
    this.draftState.study = defaultJobRequestState.study
  }

  fetchStudyEnvironmentOptionsGO() {
    this.draftState.statusDetails = Status.PENDING
  }
  fetchStudyEnvironmentOptionsOK(studyEnvironmentOptions: []) {
    this.draftState.studyEnvironment.studyEnvironmentOptions = studyEnvironmentOptions
    this.draftState.statusDetails = Status.OK
  }
  fetchStudyEnvironmentOptionsERR(error) {
    this.draftState.error = error
    this.draftState.statusDetails = Status.REJECTED
  }
  studyEnvironmentApplied(studyId, studyEnvironmentName: string, studyEnvironmentUuid: string) {
    this.draftState.studyEnvironment.studyId = studyId
    this.draftState.studyEnvironment.studyEnvironmentName = studyEnvironmentName
    this.draftState.studyEnvironment.studyEnvironmentUuid = studyEnvironmentUuid
  }
  studyEnvironmentReset() {
    this.draftState.studyEnvironment = defaultJobRequestState.studyEnvironment
  }

  changesAfterDateApplied(date: Scalars['DateTime']) {
    this.draftState.changesAfterDate = date
  }
  changesAfterDateReset() {
    this.draftState.changesAfterDate = defaultJobRequestState.changesAfterDate
  }

  changesAfterTimeApplied(time: Scalars['DateTime']) {
    this.draftState.changesAfterTime = time
  }
  changesAfterTimeReset() {
    this.draftState.changesAfterTime = defaultJobRequestState.changesAfterTime
  }

  objectIdApplied(objectId: string) {
    this.draftState.objectId = objectId
  }

  specifyUrlsApplied(specifiedUrls: string) {
    this.draftState.specifiedUrls = specifiedUrls
  }

  loadDataGO() {
    this.draftState.status = Status.PENDING
  }

  loadDataOK() {
    this.draftState.status = Status.OK
  }
  loadDataERR() {
    this.draftState.status = Status.REJECTED
  }
  fetchJobStatusRequestsGO() {
    this.draftState.status = Status.PENDING
  }
  fetchJobStatusRequestsOK(jobStatusRequests, pageSize) {
    const {totalCount, nodes} = jobStatusRequests
    const pageCount = Math.ceil((jobStatusRequests.totalCount ?? 0)/pageSize)
    const pageInfo = jobStatusRequests.pageInfo
    this.draftState.jobRequestsHistory.jobStatusRequests = nodes
    this.draftState.status = Status.OK
    this.draftState.jobRequestsHistory.totalCount = totalCount
    this.draftState.gridSession.pageCount = pageCount
    this.draftState.gridSession.pageInfo = pageInfo
  }
  fetchJobStatusRequestsERR(error) {
    this.draftState.error = error
    this.draftState.status = Status.REJECTED
  }

  pageCountApplied(pageCount: number) {
    this.draftState.gridSession.pageCount = pageCount
  }
  pageInfoApplied(pageInfo: PageInfo) {
    this.draftState.gridSession.pageInfo = pageInfo
  }

  requestIdDetailsApplied(requestId: string) {
    this.draftState.requestIdDetails = requestId
  }
  fetchJobStatusRequestDetailsGO() {
    this.draftState.statusDetails = Status.PENDING
  }
  fetchJobStatusRequestDetailsOK(jobStatusRequest) {
    this.draftState.error = defaultJobRequestState.error
    this.draftState.dataLoadRequestDetails = jobStatusRequest
    this.draftState.statusDetails = Status.OK
  }
  fetchJobStatusRequestDetailsERR(error) {
    this.draftState.dataLoadRequestDetails = defaultJobRequestState.dataLoadRequestDetails
    this.draftState.error = error
    this.draftState.statusDetails = Status.REJECTED
  }

  showFiltersToggled() {
    this.draftState.gridSession.showFilters = !this.state.gridSession.showFilters
  }

  showDetails() {
    this.draftState.showDetails = true
  }

  hideDetails() {
    this.draftState.showDetails = false
    this.draftState.dataLoadRequestDetails = null
  }

  clearForm() {
    this.draftState.jobRequestCategory = defaultJobRequestState.jobRequestCategory
    this.draftState.dataLoadTopic = defaultJobRequestState.dataLoadTopic
    this.draftState.database.databaseId = defaultJobRequestState.database.databaseId
    this.draftState.database.hostname = defaultJobRequestState.database.hostname
    this.draftState.dataVerificationHashObjectOptions = defaultJobRequestState.dataVerificationHashObjectOptions
    this.draftState.specifiedUrls = defaultJobRequestState.specifiedUrls
    this.draftState.dataVerificationHashObject = defaultJobRequestState.dataVerificationHashObject
    this.draftState.study.studyName = defaultJobRequestState.study.studyName
    this.draftState.study.studyUuid = defaultJobRequestState.study.studyUuid
    this.draftState.changesAfterDate = defaultJobRequestState.changesAfterDate
    this.draftState.changesAfterTime = defaultJobRequestState.changesAfterTime
    this.draftState.objectId = defaultJobRequestState.objectId
    this.draftState.specifiedUrls = defaultJobRequestState.specifiedUrls
    this.draftState.urlSelectionType = defaultJobRequestState.urlSelectionType
    this.draftState.studyEnvironment.studyEnvironmentName = defaultJobRequestState.studyEnvironment.studyEnvironmentName
    this.draftState.studyEnvironment.studyEnvironmentUuid = defaultJobRequestState.studyEnvironment.studyEnvironmentUuid 
    this.draftState.studyEnvironment.studyId = defaultJobRequestState.studyEnvironment.studyId
  }

  showDataLoadRequestFormOpen() {
    this.draftState.gridSession.showJobCreateRequestForm = true
  }

  showDataLoadRequestFormClose() {
    this.draftState.gridSession.showJobCreateRequestForm = false
  }
  cleanup() {
    this.draftState.gridSession.pageInfo = defaultJobRequestState.gridSession.pageInfo
  }
}

export const fetchStudyGroups = () => async function(dispatch) {
  dispatch(jobRequestAC.databaseReset())
  dispatch(jobRequestAC.fetchDatabaseStudyGroupUuidOptionsGO())
  try {
    const {data} = await getActiveAndEnabledDatabaseUrls()

    let databaseOptionTypes: DatabaseOptionType[]
    if (data.databases) {
      databaseOptionTypes = normalizeDatabaseOptions(data.databases)
      dispatch(jobRequestAC.fetchDatabaseStudyGroupUuidOptionsOK(databaseOptionTypes))
    }
  } catch (err) {
    dispatch(jobRequestAC.fetchDatabaseStudyGroupUuidOptionsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const fetchStudies = () => async function(dispatch, getState) {
  dispatch(jobRequestAC.studyReset())
  dispatch(jobRequestAC.fetchDatabaseStudyGroupStudyUuidOptionsGO())
  try {

    const studyGroupUuid = select_databaseId(getState())
    const {data} = await getStudies(studyGroupUuid)

    if (data.studies) {
      dispatch(jobRequestAC.fetchDatabaseStudyGroupStudyUuidOptionsOK(data.studies))
    }
  } catch (err) {
    dispatch(jobRequestAC.fetchDatabaseStudyGroupStudyUuidOptionsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const fetchStudyEnvironments = () => async function (dispatch, getState) {
  dispatch(jobRequestAC.studyEnvironmentReset())
  dispatch(jobRequestAC.fetchStudyEnvironmentOptionsGO())
  try {
    const studyGroupUuid = select_databaseId(getState())
    const studyUuid = select_studyUuid(getState())
    const {data} = await getStudyEnvironments(studyGroupUuid, studyUuid)

    if (data.studyEnvironments) {
      dispatch(jobRequestAC.fetchStudyEnvironmentOptionsOK(data.studyEnvironments))
    }
  } catch (err) {
    dispatch(jobRequestAC.fetchStudyEnvironmentOptionsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const fetchJobRequestOptions = () => async function (dispatch) {
  dispatch(jobRequestAC.dataLoadTopicReset())
  dispatch(jobRequestAC.databaseReset())
  dispatch(jobRequestAC.fetchJobRequestOptionsGO())
  try {
    const {data} = await getJobRequestOptions()
    if (data.jobRequestOptions) {
      dispatch(jobRequestAC.fetchJobRequestOptionsOK(data.jobRequestOptions, data.dataVerificationHashObjectOptions))
    }
  } catch (err) {
    dispatch(jobRequestAC.fetchJobRequestOptionsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const loadData = () => async function (dispatch, getState, router) {
  const category = select_jobRequestCategory(getState())
  const {value} = select_dataLoadTopic(getState())
  const databaseId = select_databaseId(getState())
  const dataVerificationHashObject = select_dataVerificationHashObject(getState())
  const studyUuid = select_studyUuid(getState())
  const objectId = select_objectId(getState())
  const changesAfter = select_changesAfter_timeDateUTC(getState())
  const {data} = await getActiveAndEnabledDatabaseUrls()
  const databaseOptions = data.databases
  const specifiedUrls = select_specifiedUrls(getState())
  const studyId = select_studyId(getState())
  let databaseIds: string[] = []

  dispatch(jobRequestAC.loadDataGO())
  try {
    switch (select_urlSelectionType(getState())) {
    case UrlSelection.ALL:
      databaseIds = databaseOptions.map(db => db.databaseId)
      break
    case UrlSelection.MULTIPLE: {
      const {invalidUrls, validDatabaseIds} = validateUrls(databaseOptions, specifiedUrls)
      if (invalidUrls.length) {
        const errorMessage = `${invalidUrls.length} invalid url(s) provided. Top 10=\n${invalidUrls.slice(0, 10).join('\n')}`
        dispatch(customMessageRequest('critical', errorMessage))
        dispatch(jobRequestAC.loadDataERR())
        return false
      }
      databaseIds.push(...validDatabaseIds)
      break
    }
    case UrlSelection.ONE:
      databaseIds.push(databaseId)
      break
    default:
      databaseIds.push(databaseId)
      break
    }

    if (!databaseIds.length) {
      dispatch(customMessageRequest('critical', 'At least one valid url is required to create a job request.'))
      dispatch(jobRequestAC.loadDataERR())
      return false
    }

    const input: JobRequestInput = {
      category: category.value,
      kafkaTopicUuid: value?.topicUuid,
      databaseIds: databaseIds,
      dataVerificationHashObject: dataVerificationHashObject || null,
      studyUuid: studyUuid || null,
      objectId: objectId.length ? objectId:  null,
      changesAfterDate: changesAfter || null,
      studyId: studyId || null,
    }

    const {data} = await jobRequest(input)
    if (data.jobRequest && data.jobRequest.requests?.length > 0) {
      dispatch(jobRequestAC.loadDataOK())
      dispatch(fetchJobStatusRequests())
      dispatch(genericRequestSuccess())
    }
    else if (data.jobRequest && data.jobRequest.requests?.length === 0) {
      const errorMessage = 'No jobs were created for invalid DB(s).'
      dispatch(customMessageRequest('critical', errorMessage))
      dispatch(jobRequestAC.loadDataERR())
    }
    else {
      throw {message: 'Server Error'}
    }
  } catch (err) {
    dispatch(jobRequestAC.loadDataERR())
    router.stateService.reload()
    dispatch(genericRequestError())
  }
}

export const fetchJobStatusRequests = () => async function(dispatch, getState) {
  const {pageSize, startCursor, endCursor, sorting, pageDirection, category} = select_router_params(getState())
  const jobRequestOptions = select_jobRequestOptions(getState())
  const {gqlPagination, gqlSorting, gqlFilter} = getJobStatusRequestsParams({
    pageSize, startCursor, endCursor, sorting, pageDirection, category, jobRequestOptions,
  })

  let progress = true
  setTimeout(() => {
    if (progress) dispatch(jobRequestAC.fetchJobStatusRequestsGO())
  }, 3000)

  try {
    const {data: {jobRequests}} = await getJobRequests(gqlPagination, gqlSorting, gqlFilter )
    progress = false
    if (jobRequests) {
      dispatch(jobRequestAC.fetchJobStatusRequestsOK(jobRequests, pageSize))
    } else if (!jobRequests) {
      throw new Error()
    }
  } catch (err) {
    progress = false
    dispatch(jobRequestAC.fetchJobStatusRequestsERR(JSON.parse(JSON.stringify(err))))
    dispatch(genericRequestError())
  }
}

export const fetchJobRequestDetails = (requestId) =>  async(dispatch) => {
  dispatch(jobRequestAC.fetchJobStatusRequestDetailsGO())
  try {
    const {data} = await jobStatusRequestDetails(requestId)
    if (data.jobRequests.nodes.length) {
      dispatch(jobRequestAC.fetchJobStatusRequestDetailsOK(data.jobRequests?.nodes[0]))
    } else if (!data.jobRequests.nodes.length) {
      throw new Error()
    }
  } catch (err) {
    dispatch(genericRequestError())
    dispatch(jobRequestAC.fetchJobStatusRequestDetailsERR(JSON.parse(JSON.stringify(err))))
  }
}

export const handleGridChange = (change) => (dispatch, getState, router) => {
  const {pagination: updatedPagination, sorting: updatedSorting, filters: updatedFilters} = change
  const {pageInfo: {startCursor, endCursor}} = select_gridSession(getState())
  const currentParams = select_router_params(getState())

  let params = {...currentParams, startCursor, endCursor}
  let doNavigate = false

  if (updatedSorting && !_isEqual(updatedSorting[0], currentParams.sorting)) {
    params = {
      ...params,
      sorting: updatedSorting[0] || null,
      pageIndex: 0,
      startCursor: null,
      endCursor: null,
      pageDirection: null,
    }
    doNavigate = true
  }

  if (updatedPagination && updatedPagination.pageSize !== currentParams.pageSize) {
    params = {
      ...params,
      pageSize: updatedPagination.pageSize,
      pageIndex: 0,
      pageDirection: null,
      startCursor: null,
      endCursor: null,
    }
    doNavigate = true
  } else if (updatedPagination && updatedPagination.pageIndex !== currentParams.pageIndex) {
    params = {
      ...params,
      pageSize: updatedPagination.pageSize,
      pageIndex: updatedPagination.pageIndex,
      pageDirection: getPageDirectionFromPageIndexChange(currentParams.pageIndex, updatedPagination.pageIndex),
    }
    doNavigate = true
  }

  if(updatedFilters) {
    params = {
      ...params,
      pageIndex: 0,
      startCursor: null,
      endCursor: null,
      [updatedFilters.name]: updatedFilters.option?.value || null,
    }
    doNavigate = true
  }

  if (doNavigate) router.stateService.go(RouterStates.jobRequests, params)
}

export const jobRequestAC = createActionCreators(JobRequest)

export default JobRequest
